import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, Signal } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { Observable, switchMap } from 'rxjs';
import { IItemBase, TBoardItemConfiguration, TWorkflowStepAssignment } from '../../../pages/items/items.interface';
import { ELoadStatus } from '../../../state/state.interface';
import { IBaseResponse } from '../../../utilities/http-utilities.service';

export interface IInlineItemStatusChipComponentState {
  workflowStepAssignmentsLoading: ELoadStatus;
  workflowStepAssignments: TBoardItemConfiguration | null;
  editItemLoading: ELoadStatus;
}

export interface IBoardMembersRequest {
  boardItemConfigurationId: number;
}

export interface IEditItem {
  currentWorkflowStepId: number;
  id: number;
}

@Injectable()
export class InlineItemStatusChipStore extends ComponentStore<IInlineItemStatusChipComponentState> {
  public readonly workflowStepAssignments: Signal<TWorkflowStepAssignment[]> = this.selectSignal(
    (state: IInlineItemStatusChipComponentState) =>
      state.workflowStepAssignments?.workflow?.workflowStepAssignments || [],
  );
  public readonly editItemLoading$: Observable<ELoadStatus> = this.select(
    (state: IInlineItemStatusChipComponentState) => state.editItemLoading,
  );
  public readonly workflowStepAssignmentsLoading: Observable<ELoadStatus> = this.select(
    (state: IInlineItemStatusChipComponentState) => state.workflowStepAssignmentsLoading,
  );

  readonly getWorkflowStepAssignments = this.effect((trigger$: Observable<IBoardMembersRequest>) =>
    trigger$.pipe(
      switchMap((params) => {
        this.patchState({ workflowStepAssignments: null, workflowStepAssignmentsLoading: ELoadStatus.loading });
        const httpParams: HttpParams = new HttpParams()
          .append('join', 'workflow')
          .append('join', 'workflow.workflowStepAssignments')
          .append('join', 'workflow.workflowStepAssignments.workflowStep||name,isDefault,statusCategory')
          .append('join', 'workflow.workflowStepAssignments.nextWorkflowStep||name,isDefault,statusCategory');

        return this.getBoardItemConfiguration(params.boardItemConfigurationId, httpParams).pipe(
          tapResponse(
            (response: IBaseResponse<TBoardItemConfiguration>): void => {
              this.patchState({
                workflowStepAssignments: response.data,
                workflowStepAssignmentsLoading: ELoadStatus.success,
              });
            },
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly editItemCurrentWorkflowStepId = this.effect((trigger$: Observable<IEditItem>) =>
    trigger$.pipe(
      switchMap((editItem: IEditItem) => {
        this.patchState({ editItemLoading: ELoadStatus.loading });
        const params = new HttpParams().set('preventIsQuickColumnUpdate', true);

        return this.editItem(editItem, params).pipe(
          tapResponse(
            () =>
              this.patchState({
                editItemLoading: ELoadStatus.success,
              }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  constructor(private readonly http: HttpClient) {
    super({
      editItemLoading: ELoadStatus.initial,
      workflowStepAssignments: null,
      workflowStepAssignmentsLoading: ELoadStatus.initial,
    });
  }

  private getBoardItemConfiguration(
    boardItemConfigurationId: number,
    params: HttpParams,
  ): Observable<IBaseResponse<TBoardItemConfiguration>> {
    return this.http.get<IBaseResponse<TBoardItemConfiguration>>(
      `board-item-configurations/${boardItemConfigurationId}`,
      {
        params,
      },
    );
  }

  private editItem(item: IEditItem, params: HttpParams): Observable<IBaseResponse<IItemBase>> {
    return this.http.patch<IBaseResponse<IItemBase>>(
      `items/${item.id}`,
      {
        currentWorkflowStepId: item.currentWorkflowStepId,
      },
      { params },
    );
  }
}
